import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatStep, MatStepper } from '@angular/material/stepper';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { ObjectTypeInfoViewModel, QueryColumn, QueryParameter } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
  ProtocolViewerService,
} from '@barthauer/abc/messages';
import { ParameterHelper } from '@barthauer/abc/queries/parameters/core';
import { AbcComboboxComponent, IStorage, _AbcGridColumn, _AbcGridComponent } from '@barthauer/abc/ui-components';
import {
  GroupSelectionComponent,
  ObjectSelectionComponent,
  ObjectSelectionGroup,
} from '@barthauer/abc/ui-frameworks/object-selection';
import { ProfileService } from '@barthauer/basys/core/authentication';
import { addDays, differenceInDays } from 'date-fns';
import { dxDataGridColumn } from 'devextreme/ui/data_grid';
import { BehaviorSubject, merge, Observable, of, Subject, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  mapTo,
  publishReplay,
  refCount,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { DataQueryOPathes } from '../create-works-wizard/models/data-query-opathes';
import { MaintenanceOrder, ResultWorkGeneration } from '../create-works-wizard/models/helpers';
import { MtBase } from '../create-works-wizard/models/MtBase';
import { MtObjectWork, MtWork } from '../create-works-wizard/models/MtWork';
import {
  CyclicWorkCalculator,
  MtWorkGenerationInfo,
  MtWorkQueryInfo,
} from '../create-works-wizard/models/MtWorkGenerationInfo';
import { MtResource } from '../create-works-wizard/models/resource.model';
import { WorkGeneratorMode, WorkState, WorkWizardOptions } from '../create-works-wizard/models/WorkWizardOptions';
import { WorkService } from '../create-works-wizard/services/work.service';
import { ConfigurationComponent } from './pages/configuration/configuration.component';
import { ValueChangedEvent } from 'devextreme/ui/check_box';

@Component({
  selector: 'basys-opera-wizards-create-orders-wizard',
  templateUrl: './create-orders-wizard.component.html',
  styleUrls: ['./create-orders-wizard.component.scss'],
  standalone: false,
})
export class CreateOrdersWizardComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() auftragStatus?: string;
  @Output() closeWizardDialog = new EventEmitter();

  @ViewChild('stepper', { read: MatStepper, static: false }) stepper!: MatStepper;
  @ViewChild('stepMainData', { read: MatStep, static: false }) stepMainData!: MatStep;
  @ViewChild('stepResourcesSelection', { read: MatStep, static: false }) stepResourcesSelection!: MatStep;
  @ViewChild('stepObjectSelection', { read: MatStep, static: false }) stepObjectSelection!: MatStep;
  @ViewChild('stepAuftrag', { read: MatStep, static: false }) stepAuftrag!: MatStep;
  @ViewChild('configurationComponent', { static: false }) configurationComponent!: ConfigurationComponent;
  @ViewChild('groupSelectionComponent', { read: GroupSelectionComponent, static: false })
  groupSelectionComponent!: GroupSelectionComponent;
  @ViewChild('groupSelectionComponent2', { read: GroupSelectionComponent, static: false })
  groupSelectionComponent2!: GroupSelectionComponent;
  @ViewChild('auftragStatusCombobox', { read: AbcComboboxComponent, static: false })
  auftragStatusCombobox!: AbcComboboxComponent;

  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  public assetsCount = signal<number>(0);
  public resCount = signal<number>(0);
  public isReadOnly = signal<boolean>(false);
  public buttonStatus = signal<boolean>(false);
  public displayBusybar = signal<boolean>(false);
  public firstTitle = signal<string>('');
  public textButton = signal<string>('');
  public isLinear = true;
  public isEditable = true;
  public auftragStatusData: any;
  public selectedGeneratorMode: WorkGeneratorMode = WorkGeneratorMode.cyclic;
  public objectSelectionGroups$!: Observable<ObjectSelectionGroup[]>;
  public anotherObjectSelectionGroups$!: Observable<ObjectSelectionGroup[]>;
  public isLoadingGroups$!: Observable<boolean>;
  public isLoadingAnotherGroups$!: Observable<boolean>;
  public selectChildrenResources: boolean = true;
  public selectReferencedResources: boolean = false;
  public auftragsDate!: Date;
  public manualEarliestExecDate!: Date;
  public manualScheduledExecDate!: Date;
  public manualLatestExecDate!: Date;
  public executionTimeInDays: number | null;

  private auftragsNameResult?: string;
  private alive = true;
  private auftragsId?: string;
  private workwizardOptions: WorkWizardOptions = new WorkWizardOptions();
  private newAuftragName?: string = '';
  private newAuftragNumber?: string = '';
  private newAuftragRemark?: string = '';
  private unsubscribe$ = new Subject<void>();
  private lastWorkQueryInfo?: MtWorkQueryInfo;
  private readonly objSelectionGroupName = 'WorkGenerationInfos';
  private readonly objSelectionGroupName2 = 'ResourcesGenerationInfos';
  private readonly availableWorksObjSelectionGroupName = 'AvailableWorkGenerationInfos';
  private readonly refreshGroupsToken$ = new BehaviorSubject(false);
  private readonly refreshAnotherGroupsToken$ = new BehaviorSubject(false);
  private readonly lookupNewObjectTypeTrigger$ = new BehaviorSubject(0);
  private gridLayoutStorage: IStorage;
  private preselectedKeys: any[] = [];
  private selectedResourcesIds: string[];
  private selectedResourceKeys: string[];
  private matchingResources: MtResource[];
  private statusList: string[] = ['geplant', 'erteilt', 'erledigt', 'archiviert'];
  private workTemplateObjectTypeDefinition: string | undefined;
  private resourceSelectionControl: ObjectSelectionComponent = undefined;
  private idRefResourceParentName: string = 'IdRefResourceParent';
  private resourceStateTypeName: string = 'Type';
  private refResourceIdsColumnName: string = 'RefResourceIds';

  constructor(
    private abcCore: AbcCore,
    private workService: WorkService,
    private messageBoxService: MessageBoxService,
    private resourceService: ResourceManagerService,
    private protocolViewerService: ProtocolViewerService,
    private profileService: ProfileService,
  ) {
    this.resAsync = this.resourceService.getResourceSet('opera-wizards');
    this.workService.generationFinished.pipe(takeUntil(this.unsubscribe$)).subscribe((res: ResultWorkGeneration) => {
      this.displayBusybar.set(false);
      this.firstTitle.set(this.res.get('OperationFinishedSuccessfully'));
      this.buttonStatus.set(false);
      this.generateProtocol(res);
    });

    this.workService.generationFailed
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((err) => this.onWorkGenerationFailed(err));

    this.workService.raiseErrorInfo
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((msg) => this.showMessageAsHtml(msg, MessageMode.Error));

    this.workService.raiseWarningInfo
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((msg) => this.showWarningMessageAsHtml(msg, MessageMode.Warning));

    this.workService.raiseInfo.pipe(takeUntil(this.unsubscribe$)).subscribe((msg) => this.showMessageAsHtml(msg));
  }

  get buttonClass() {
    return this.isReadOnly() ? 'readonly-button' : '';
  }

  async ngOnInit() {
    this.res = await this.resAsync;
    this.firstTitle.set(this.res.get('WizardInformationReady'));
    this.textButton.set(this.res.get('ButtonTextStartExecution'));
    this.auftragsDate = new Date();
    this.manualEarliestExecDate = this.auftragsDate;
    this.manualScheduledExecDate = this.auftragsDate;

    this.workService
      .queryPicklistItems('AuftragsStatus')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => (this.auftragStatusData = result.Data));

    this.objectSelectionGroups$ = this.refreshGroupsToken$.pipe(
      switchMap((allowRefresh) =>
        (allowRefresh ? this.createCurrentObjectSelectionGroups() : of(new Array<ObjectSelectionGroup>())).pipe(
          tap(
            () =>
              (this.lastWorkQueryInfo = {
                GeneratorMode: this.selectedGeneratorMode,
                WorkTypeId: this.configurationComponent?.workTypeId,
                ManualEarliestWorkExecDate: this.manualEarliestExecDate,
                ManualScheduledWorkExecDate: this.manualScheduledExecDate,
                ManualLatestWorkExecDate: this.manualLatestExecDate,
              }),
          ),
          catchError((err) => {
            this.abcCore.error.publish(err);
            return of(new Array<ObjectSelectionGroup>());
          }),
        ),
      ),
    );

    this.isLoadingGroups$ = merge(
      this.refreshGroupsToken$.pipe(mapTo(true)),
      this.objectSelectionGroups$.pipe(
        catchError(() => of(undefined)),
        mapTo(false),
      ),
    ).pipe(publishReplay(1), refCount());

    this.anotherObjectSelectionGroups$ = this.refreshAnotherGroupsToken$.pipe(
      switchMap((allowRefresh) =>
        (allowRefresh ? this.createAnotherObjectSelectionGroups() : of([])).pipe(
          catchError((err) => {
            this.abcCore.error.publish(err);
            return of([]);
          }),
        ),
      ),
    );

    this.anotherObjectSelectionGroups$.subscribe((data) => {
      setTimeout(() => {
        this.initResourceSelectionHook();
      });
    });

    this.isLoadingAnotherGroups$ = merge(
      this.refreshAnotherGroupsToken$.pipe(mapTo(true)),
      this.anotherObjectSelectionGroups$.pipe(
        catchError(() => of(undefined)),
        mapTo(false),
      ),
    ).pipe(publishReplay(1), refCount());

    this.lookupNewObjectTypeTrigger$
      .pipe(
        concatMap((newObjectTypeNumber) =>
          newObjectTypeNumber > 0
            ? this.abcCore.objectSpace.getObjectType(newObjectTypeNumber)
            : of(null as unknown as ObjectTypeInfoViewModel),
        ),
        takeUntil(this.unsubscribe$),
      )
      .subscribe({
        next: (otViewModel) => {
          if (otViewModel) {
            this.objectTypeDesignations.set(otViewModel.value, otViewModel.displayText);
          }
        },
      });

    const profileInfo = await this.profileService.getCurrentProfile(true).toPromise();
    this.isReadOnly.set(ConvertHelper.toBoolean(profileInfo?.settings?.Permissions?.isReadOnly));
  }

  ngAfterViewInit(): void {
    this.selectChildrenResources = true;
    this.selectReferencedResources = false;

    setTimeout(() => {
      this.initResourceSelectionHook();
    });
  }

  private initResourceSelectionHook() {
    const comp = this.groupSelectionComponent2?.getObjectSelectionControl(this.objSelectionGroupName2);

    if (!comp) {
      return;
    }

    this.resourceSelectionControl = comp;

    this.resourceSelectionControl.selectionChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((events) => {
      const selectedKeys = events.selected ?? [];
      this.selectRelatedResources(selectedKeys);
    });
  }

  private selectRelatedResources(selectedKeys: any[]) {
    let keysRelatedResources = [];
    if (this.selectChildrenResources || this.selectReferencedResources) {
      const dataRows = this.resourceSelectionControl?.objectSelectionGrid?.instance?.getDataSource()?.items();
      const keyExpr = this.resourceSelectionControl?.keyExpr;

      keysRelatedResources = this.workService.getRelatedResources(
        selectedKeys,
        dataRows,
        keyExpr,
        this.idRefResourceParentName,
        this.refResourceIdsColumnName,
        this.resourceStateTypeName,
        this.selectChildrenResources,
        this.selectReferencedResources,
      );
    }

    if (keysRelatedResources?.length) {
      this.resourceSelectionControl.applyExternalSelection(keysRelatedResources);
    }
  }

  public onWorkTypeIdChange(execTimeInDays: number | null): void {
    this.executionTimeInDays = execTimeInDays;
    this.manualScheduledExecDate = this.auftragsDate;

    if (execTimeInDays) {
      this.manualLatestExecDate = this.calculateLatestExecutionDate(this.manualScheduledExecDate, execTimeInDays);
    }
  }

  private calculateLatestExecutionDate(currentDate: Date, executionTimeInDays: number): Date {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + executionTimeInDays);
    return newDate;
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.refreshGroupsToken$.complete();
    this.refreshAnotherGroupsToken$.complete();
    this.lookupNewObjectTypeTrigger$.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public stepSelectionChange(eventArgs: any): void {
    if (eventArgs.selectedStep === this.stepObjectSelection) {
      if (
        !this.lastWorkQueryInfo ||
        this.lastWorkQueryInfo.GeneratorMode !== this.selectedGeneratorMode ||
        !this.lastWorkQueryInfo.WorkTypeId ||
        this.lastWorkQueryInfo.WorkTypeId !== this.configurationComponent?.workTypeId ||
        (this.selectedGeneratorMode === WorkGeneratorMode.manual &&
          (this.lastWorkQueryInfo.ManualEarliestWorkExecDate !== this.manualEarliestExecDate ||
            this.lastWorkQueryInfo.ManualLatestWorkExecDate !== this.manualLatestExecDate ||
            this.lastWorkQueryInfo.ManualScheduledWorkExecDate !== this.manualScheduledExecDate))
      ) {
        this.refreshObjectSelection();
      }
    } else if (eventArgs.selectedStep === this.stepResourcesSelection) {
      this.refreshAnotherObjectSelection();
    }
  }

  private refreshObjectSelection(): void {
    this.refreshGroupsToken$.next(true);
  }

  private refreshAnotherObjectSelection(): void {
    this.refreshAnotherGroupsToken$.next(true);
  }

  onWorkGenerationFailed(err: any): void {
    this.displayBusybar.set(false);
    this.firstTitle.set(this.res.get('OperationFinishedWithErrors'));
    this.showMessageAsHtml(err.message + '<br/>' + err.stack, MessageMode.Error);
  }

  public openHelp() {
    this.abcCore.openWindow('http://help.barthauer.de/BaSYS/9.26.1/Web/opera-manager');
  }

  public onEarliestExecDateChanged(eventArgs: any): void {
    this.manualEarliestExecDate = new Date(ConvertHelper.toString(eventArgs));
  }

  public onScheduledExecDateChanged(eventArgs: any): void {
    this.manualScheduledExecDate = new Date(ConvertHelper.toString(eventArgs));
  }

  public onLatestExecDateChanged(eventArgs: any): void {
    this.manualLatestExecDate = new Date(ConvertHelper.toString(eventArgs));
  }

  manualExecTimeInDays?: number;
  onExecTimeInDaysChanged(eventArgs: any): void {
    this.manualExecTimeInDays = Number(ConvertHelper.toString(eventArgs.newValue));
  }

  private createCurrentObjectSelectionGroups(): Observable<ObjectSelectionGroup[]> {
    this.assetsCount.set(0);
    const objectDefinition = this.configurationComponent.currentWorkType?.objectTypeDefinition;
    if (objectDefinition) {
      this.workTemplateObjectTypeDefinition = `(Id In (${objectDefinition}.Id) Or IdMasterObject In (${objectDefinition}.Id)) And ParentId=$CurrentMunicipalityVariantId`;
    } else {
      this.workTemplateObjectTypeDefinition = undefined;
    }

    const workTypeId = this.configurationComponent.workTypeId ?? '';
    if (this.selectedGeneratorMode !== 'availableWorks') {
      if (!workTypeId) {
        return throwError(new Error('No WorkType selected!'));
      }
    }

    const dataQueryOPathes = new DataQueryOPathes();
    let oPathFilter: string;
    let additionalQueryColumns: QueryColumn[] = [];
    let additionalGridColumns: dxDataGridColumn[] = [];
    let additionalQueryParameters: QueryParameter[] = [];

    switch (this.selectedGeneratorMode) {
      case WorkGeneratorMode.cyclic: {
        oPathFilter = `ParentId=$CurrentMunicipalityVariantId And Exists(WorkTypes[WorkTypeId = $${dataQueryOPathes.paramName_WorkTypeId}])`;

        additionalQueryColumns = [
          { Name: 'MtObjWorkTypeId', OPath: `WorkTypes[WorkTypeId = $${dataQueryOPathes.paramName_WorkTypeId}].Id` },
          { Name: 'EarliestWorkExecDate', OPath: dataQueryOPathes.cyclicMode.mtObjectEarliestExecutionDateOPath },
          { Name: 'ScheduledWorkExecDate', OPath: dataQueryOPathes.cyclicMode.mtObjectScheduledExecutionDateOPath },
          { Name: 'LatestWorkExecDate', OPath: dataQueryOPathes.cyclicMode.mtObjectLatestExecutionDateOPath },
          {
            Name: 'WorkExecTimeInDays',
            OPath: `CaseNull(WorkTypes[WorkTypeId = $${dataQueryOPathes.paramName_WorkTypeId}].ExecTime, $${dataQueryOPathes.paramName_WorkTypeExecTimeInDays})`,
          },
          {
            Name: 'HasNoExistingWorksFilter',
            OPath: dataQueryOPathes.cyclicMode.mtObjectHasNoExistingWorksFilterOPath,
            IsFilter: true,
          },
          {
            Name: 'IsValidEarliestExecutionDateFilter',
            OPath: dataQueryOPathes.cyclicMode.mtObjectIsValidEarliestExecutionDateFilterOPath,
            IsFilter: true,
          },
          {
            Name: 'AllowOnlyValidMtObjWorkTypes',
            OPath: dataQueryOPathes.cyclicMode.mtObjectAllowOnlyValidMtObjWorkTypesFilterOPath,
            IsFilter: true,
          },
          {
            Name: 'WW_Kanalart',
            OPath:
              'CaseTrue(IsTypeValue(GeObjectType,AbwLeitung), Type(AbwLeitung)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].Kanalart, IsTypeValue(GeObjectType,AbwKnoten), Type(AbwKnoten)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].Kanalart)',
          },
          {
            Name: 'WW_AnschlussFunktion',
            OPath:
              'CaseNull(Type(AbwLeitung)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].GetValue(Anschlussfunktion), Type(AbwKnoten)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].GetValue(Anschlussfunktion))',
          },
          {
            Name: 'Fachbereich',
            OPath: `CaseWhen(Kind, 0, 'Abwasser', 1, 'Wasser', 2, 'Gas', 3, 'Kabel', 4, 'GeoObjekt', 5, 'Straße', ref:Kind)`,
          },
          { Name: 'StrVerkehrsflaeche_Art', OPath: 'ref:Type(StrFlaeche)[Id = ^.IdMasterObject].Art' },
          { Name: 'StrVerkehrsflaeche_Belagsart', OPath: 'ref:Type(StrFlaeche)[Id = ^.IdMasterObject].Belagsart' },
          {
            Name: 'StrVerkehrsflaeche_Strasse',
            OPath: 'Type(StrFlaeche)[Id = ^.IdMasterObject].Abschnitt.Strasse.Name',
          },
        ];

        additionalGridColumns = [
          { dataField: 'MtObjWorkTypeId', visible: false, allowFiltering: false, showInColumnChooser: false },
          { dataField: 'WorkExecTimeInDays', visible: false, allowFiltering: false, showInColumnChooser: false },
          { dataField: 'WW_Kanalart', caption: this.res.get('ChannelArt'), visible: false, showInColumnChooser: true },
          {
            dataField: 'WW_AnschlussFunktion',
            caption: this.res.get('ConnectionFunction'),
            visible: false,
            showInColumnChooser: true,
          },
          { dataField: 'Fachbereich', caption: this.res.get('Department'), visible: false, showInColumnChooser: true },
          {
            dataField: 'StrVerkehrsflaeche_Art',
            caption: this.res.get('TrafficAreaArt'),
            visible: false,
            showInColumnChooser: true,
          },
          {
            dataField: 'StrVerkehrsflaeche_Belagsart',
            caption: this.res.get('TrafficAreaPavementType'),
            visible: false,
            showInColumnChooser: true,
          },
          {
            dataField: 'StrVerkehrsflaeche_Strasse',
            caption: this.res.get('TrafficAreaStreet'),
            visible: false,
            showInColumnChooser: true,
          },
        ];

        additionalQueryParameters = [
          {
            Name: `${dataQueryOPathes.paramName_WorkTypeExecTimeInDays}`,
            Value: Object(
              this.configurationComponent.currentWorkType?.execTime ?? CyclicWorkCalculator.DefaultWorkExecTimeInDays,
            ),
          },
          {
            Name: `${dataQueryOPathes.paramName_WorkTypeLeadTimeInDays}`,
            Value: Object(
              this.configurationComponent.currentWorkType?.leadTime ?? CyclicWorkCalculator.DefaultWorkLeadTimeInDays,
            ),
          },
          { Name: `${dataQueryOPathes.paramName_TodayDate}`, Value: ParameterHelper.getDateValue(new Date()) },
        ];
        break;
      }
      case WorkGeneratorMode.manual: {
        oPathFilter = 'ParentId=$CurrentMunicipalityVariantId';

        additionalQueryColumns = [
          { Name: 'MtObjWorkTypeId', OPath: 'null' },
          { Name: 'WorkExecTimeInDays', OPath: `$${dataQueryOPathes.manualMode.paramName_WorkExecTimeInDays}` },
          {
            Name: 'HasNoExistingWorksFilter',
            OPath: dataQueryOPathes.manualMode.mtObjectHasNoExistingWorksFilterOPath,
            IsFilter: true,
          },
          {
            Name: 'WW_Kanalart',
            OPath:
              'CaseTrue(IsTypeValue(GeObjectType,AbwLeitung), Type(AbwLeitung)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].Kanalart, IsTypeValue(GeObjectType,AbwKnoten), Type(AbwKnoten)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].Kanalart)',
          },
          {
            Name: 'WW_Anschlussfunktion',
            OPath:
              'CaseNull(Type(AbwLeitung)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].GetValue(Anschlussfunktion), Type(AbwKnoten)[Id=^.GeObjectId And ParentId=$CurrentMunicipalityVariantId].GetValue(Anschlussfunktion))',
          },
          {
            Name: 'Fachbereich',
            OPath: `CaseWhen(Kind, 0, 'Abwasser', 1, 'Wasser', 2, 'Gas', 3, 'Kabel', 4, 'GeoObjekt', 5, 'Straße', ref:Kind)`,
          },
          { Name: 'StrVerkehrsflaeche_Art', OPath: 'ref:Type(StrFlaeche)[Id = ^.IdMasterObject].Art' },
          { Name: 'StrVerkehrsflaeche_Belagsart', OPath: 'ref:Type(StrFlaeche)[Id = ^.IdMasterObject].Belagsart' },
          {
            Name: 'StrVerkehrsflaeche_Strasse',
            OPath: 'Type(StrFlaeche)[Id = ^.IdMasterObject].Abschnitt.Strasse.Name',
          },
        ];

        additionalGridColumns = [
          { dataField: 'MtObjWorkTypeId', visible: false, allowFiltering: false, showInColumnChooser: false },
          { dataField: 'WorkExecTimeInDays', visible: false, allowFiltering: false, showInColumnChooser: false },
          { dataField: 'WW_Kanalart', caption: this.res.get('ChannelArt'), visible: false, showInColumnChooser: true },
          {
            dataField: 'WW_Anschlussfunktion',
            caption: this.res.get('ConnectionFunction'),
            visible: false,
            showInColumnChooser: true,
          },
          { dataField: 'Fachbereich', caption: this.res.get('Department'), visible: false, showInColumnChooser: true },
          {
            dataField: 'StrVerkehrsflaeche_Art',
            caption: this.res.get('TrafficAreaArt'),
            visible: false,
            showInColumnChooser: true,
          },
          {
            dataField: 'StrVerkehrsflaeche_Belagsart',
            caption: this.res.get('TrafficAreaPavementType'),
            visible: false,
            showInColumnChooser: true,
          },
          {
            dataField: 'StrVerkehrsflaeche_Strasse',
            caption: this.res.get('TrafficAreaStreet'),
            visible: false,
            showInColumnChooser: true,
          },
        ];

        const orderedExecDates: Date[] = [
          this.manualEarliestExecDate,
          this.manualScheduledExecDate,
          this.manualLatestExecDate,
        ]
          .filter((d) => d !== undefined)
          .map((d) => d as Date)
          .sort();

        let earliestExecDate = new Date();
        let scheduledExecDate = new Date();
        let latestExecDate = new Date();

        if (Array.isArray(orderedExecDates) && orderedExecDates.length) {
          earliestExecDate = orderedExecDates[0];
          scheduledExecDate = this.manualScheduledExecDate ?? earliestExecDate;
          latestExecDate = orderedExecDates[orderedExecDates.length - 1];
        }

        let execTime = Math.abs(differenceInDays(latestExecDate, earliestExecDate));

        if (execTime === 0) {
          execTime = CyclicWorkCalculator.DefaultWorkExecTimeInDays;
          latestExecDate = addDays(earliestExecDate, execTime);
        }

        additionalQueryParameters = [
          {
            Name: `${dataQueryOPathes.manualMode.paramName_EarliestWorkExecDate}`,
            Value: ParameterHelper.getDateValue(earliestExecDate),
          },
          {
            Name: `${dataQueryOPathes.manualMode.paramName_ScheduledWorkExecDate}`,
            Value: ParameterHelper.getDateValue(scheduledExecDate),
          },
          {
            Name: `${dataQueryOPathes.manualMode.paramName_LatestWorkExecDate}`,
            Value: ParameterHelper.getDateValue(latestExecDate),
          },
          { Name: `${dataQueryOPathes.manualMode.paramName_WorkExecTimeInDays}`, Value: Object(execTime) },
        ];
        break;
      }

      case WorkGeneratorMode.availableWorks: {
        oPathFilter = `Object.ParentId=$CurrentMunicipalityVariantId AND IsNull(AuftragsId)`;
        additionalQueryColumns = [
          {
            Name: 'Arbeitsvorlage',
            OPath: 'WorkType.Designation',
          },
          {
            Name: 'Fachbereich',
            OPath: `CaseWhen(Object.Kind, 0, 'Abwasser', 1, 'Wasser', 2, 'Gas', 3, 'Kabel', 4, 'Object.GeoObjekt', 5, 'Object.Straße', ref:Object.Kind)`,
          },
          {
            Name: 'Ausführung ab',
            OPath: 'EarliestExecutionDate',
          },
          {
            Name: 'Ausführung geplant',
            OPath: 'ScheduledExecutionDate',
          },
          {
            Name: 'Ausführung bis',
            OPath: 'LatestExecutionDate',
          },
          { Name: 'StrVerkehrsflaeche_Art', OPath: 'ref:Type(StrFlaeche)[Id = ^.Object.IdMasterObject].Art' },
          {
            Name: 'StrVerkehrsflaeche_Belagsart',
            OPath: 'ref:Type(StrFlaeche)[Id = ^.Object.IdMasterObject].Belagsart',
          },
          {
            Name: 'StrVerkehrsflaeche_Strasse',
            OPath: 'Type(StrFlaeche)[Id = ^.Object.IdMasterObject].Abschnitt.Strasse.Name',
          },
        ];
        additionalGridColumns = [
          {
            dataField: 'Arbeitsvorlage',
            caption: this.res.get('WorksTemplate'),
            visible: false,
            showInColumnChooser: true,
          },
          { dataField: 'Fachbereich', caption: this.res.get('Department'), visible: false, showInColumnChooser: true },
          {
            dataField: 'Ausführung ab',
            caption: this.res.get('ExecutionFrom'),
            visible: false,
            showInColumnChooser: true,
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'Ausführung geplant',
            caption: this.res.get('ExecutionPlanned'),
            visible: false,
            showInColumnChooser: true,
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'Ausführung bis',
            caption: this.res.get('ExecutionTill'),
            visible: false,
            showInColumnChooser: true,
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'StrVerkehrsflaeche_Art',
            caption: this.res.get('TrafficAreaArt'),
            visible: false,
            showInColumnChooser: true,
          },
          {
            dataField: 'StrVerkehrsflaeche_Belagsart',
            caption: this.res.get('TrafficAreaPavementType'),
            visible: false,
            showInColumnChooser: true,
          },
          {
            dataField: 'StrVerkehrsflaeche_Strasse',
            caption: this.res.get('TrafficAreaStreet'),
            visible: false,
            showInColumnChooser: true,
          },
        ];
        additionalQueryParameters = [];
        break;
      }

      default:
        return throwError(new Error('Invalid Generator mode!'));
    }

    const worksToGenerateGroup: ObjectSelectionGroup = {
      name: this.objSelectionGroupName,
      designation: 'Anlagen',
      layoutStorage: this.gridLayoutStorage,
      layoutStorageKey: 'create-orders-wizard.' + this.objSelectionGroupName,
      objectTypeNameOrQueryInfo: {
        objectSpace: 'BaSYS.Complete',
        objectType: 'MtObject',
        oPathFilter: this.workTemplateObjectTypeDefinition ? this.workTemplateObjectTypeDefinition : oPathFilter,
        columns: [
          { Name: 'Id', OPath: 'Id' },
          { Name: 'ObjectType', OPath: 'ObjectType' },
          { Name: 'Designation', OPath: 'Designation' },
          { Name: 'MasterObjectType', OPath: 'GeObjectType' },
          { Name: 'MasterObjectId', OPath: 'GeObjectId' },
          { Name: 'Ref_Strasse', OPath: 'ref:IdRefStrasse' },
          { Name: 'Status', OPath: 'ref:Status' },
          { Name: 'Ref_Ortsteil', OPath: 'ref:IdRefOrtsteil' },
          { Name: 'Ref_Arbeitsbereich', OPath: 'ref:IdRefArbeitsbereich' },
          { Name: 'MtWorkTypeId', OPath: '$WorkTypeId' },
          ...additionalQueryColumns,
        ],
        columnHeaders: [
          { dataField: 'Designation', caption: this.res.get('Designation'), sortOrder: 'asc' },
          {
            dataField: 'Id',
            dataType: 'string',
            visible: false,
            allowFiltering: true,
            showInColumnChooser: false,
          },
          {
            dataField: 'MasterObjectType',
            dataType: 'number',
            visible: false,
            allowFiltering: false,
            showInColumnChooser: false,
          },
          {
            dataField: 'MasterObjectId',
            dataType: 'string',
            visible: false,
            allowFiltering: true,
            showInColumnChooser: false,
          },
          {
            dataField: 'MasterObjectTypeDesignation',
            caption: this.res.get('AssetType'),
            calculateCellValue: (rowData: any) =>
              this.calcObjectTypeDesignation(rowData, this.objectTypeDesignations, this.lookupNewObjectTypeTrigger$),
            allowSorting: true,
            allowFiltering: true,
            allowSearch: true,
            allowGrouping: true,
          },
          { dataField: 'Ref_Strasse', caption: this.res.get('Street') },
          { dataField: 'Ref_Ortsteil', caption: this.res.get('District') },
          { dataField: 'Status', caption: this.res.get('Status') },
          { dataField: 'Ref_Arbeitsbereich', caption: this.res.get('WorkArea'), visible: false },
          { dataField: 'MtWorkTypeId', visible: false, allowFiltering: false, showInColumnChooser: false },
          ...additionalGridColumns,
        ],
        parameters: [{ Name: 'WorkTypeId', Value: Object(workTypeId ?? null) }, ...additionalQueryParameters],
      },
      preselectionKeyExpr: 'MasterObjectId',
      preselectedKeys: this.preselectedKeys,
    };

    const availableWorksToAssignGroup: ObjectSelectionGroup = {
      name: this.objSelectionGroupName,
      designation: 'Arbeiten',
      layoutStorage: this.gridLayoutStorage,
      layoutStorageKey: 'create-orders-wizard.' + this.availableWorksObjSelectionGroupName,
      objectTypeNameOrQueryInfo: {
        objectSpace: 'BaSYS.Complete',
        objectType: 'MtWork',
        oPathFilter: oPathFilter,
        columns: [
          { Name: 'Designation', OPath: 'Designation' },
          { Name: 'Anlage', OPath: 'Object.Designation' },
          { Name: 'MasterObjectType', OPath: 'Object.GeObjectType' },
          { Name: 'Ref_Strasse', OPath: 'ref:Object.IdRefStrasse' },
          { Name: 'Status', OPath: 'ref:State' },
          { Name: 'Ref_Ortsteil', OPath: 'ref:Object.IdRefOrtsteil' },
          { Name: 'ObjWorkType_Designation', OPath: 'ObjWorkType.Designation' },
          ...additionalQueryColumns,
        ],
        columnHeaders: [
          { dataField: 'Designation', caption: this.res.get('Designation'), sortOrder: 'asc' },
          {
            dataField: 'Anlage',
            caption: this.res.get('Asset'),
            allowSorting: true,
            allowFiltering: true,
            allowSearch: true,
            allowGrouping: true,
          },
          {
            dataField: 'MasterObjectTypeDesignation',
            caption: this.res.get('AssetType'),
            calculateCellValue: (rowData: any) =>
              this.calcObjectTypeDesignation(rowData, this.objectTypeDesignations, this.lookupNewObjectTypeTrigger$),
            allowSorting: true,
            allowFiltering: true,
            allowSearch: true,
            allowGrouping: true,
          },
          { dataField: 'Ref_Strasse', caption: this.res.get('Street') },
          { dataField: 'Ref_Ortsteil', caption: this.res.get('District') },
          { dataField: 'Status', caption: this.res.get('Status') },
          { dataField: 'ObjWorkType_Designation', caption: this.res.get('MaintenanceCycle') },
          ...additionalGridColumns,
        ],
        parameters: [],
      },
    };

    if (this.selectedGeneratorMode === WorkGeneratorMode.availableWorks) {
      return of([availableWorksToAssignGroup]);
    } else {
      return of([worksToGenerateGroup]);
    }
  }

  private createAnotherObjectSelectionGroups(): Observable<ObjectSelectionGroup[]> {
    let additionalQueryColumns: QueryColumn[] = [];
    let additionalGridColumns: dxDataGridColumn[] = [];

    additionalQueryColumns = [
      { Name: 'Code', OPath: 'Code' },
      { Name: 'Kostenstelle', OPath: 'CostCenter' },
      { Name: 'Ressourcen-Typ', OPath: 'ref:IdRefResourceType' },
      { Name: 'Login', OPath: 'Login' },
      { Name: 'Einheit', OPath: 'DefaultUnit' },
      { Name: 'Einheitspreis', OPath: 'DefaultUnitPrice' },
      { Name: 'Übergeordnete Ressource', OPath: 'ref:IdRefResourceParent' },
      { Name: this.idRefResourceParentName, OPath: 'IdRefResourceParent' },
      { Name: this.resourceStateTypeName, OPath: 'Type' },
      {
        Name: this.refResourceIdsColumnName,
        OPath: "ReferencedResources.ReferencedResource[Type<>0].JoinPath(Id, ',')",
      },
    ];

    additionalGridColumns = [
      { dataField: 'Code', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: 'Kostenstelle', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: 'Ressourcen-Typ', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: 'Login', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: 'Einheit', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: 'Einheitspreis', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: 'Übergeordnete Ressource', visible: false, allowFiltering: true, showInColumnChooser: true },
      { dataField: this.idRefResourceParentName, visible: false, allowFiltering: true, showInColumnChooser: false },
      { dataField: this.resourceStateTypeName, visible: false, allowFiltering: true, showInColumnChooser: false },
      { dataField: this.refResourceIdsColumnName, visible: false, allowFiltering: true, showInColumnChooser: false },
    ];

    const resToGenerateGroup: ObjectSelectionGroup = {
      name: this.objSelectionGroupName2,
      designation: 'Ressourcen',
      layoutStorage: this.gridLayoutStorage,
      layoutStorageKey: 'create-orders-wizard.' + this.objSelectionGroupName2,
      objectTypeNameOrQueryInfo: {
        objectSpace: 'BaSYS.Complete',
        objectType: 'MtResource',
        columns: [
          { Name: 'Id', OPath: 'Id' },
          { Name: 'ObjectType', OPath: 'ObjectType' },
          { Name: 'Code', OPath: 'Code' },
          { Name: 'Name', OPath: 'Name' },
          { Name: 'Kind', OPath: 'ref:Kind' },
          { Name: 'CostCenter', OPath: 'CostCenter' },
          { Name: 'DefaultUnit', OPath: 'DefaultUnit' },
          { Name: 'DefaultUnitPrice', OPath: 'DefaultUnitPrice' },
          { Name: 'Login', OPath: 'Login' },
          { Name: 'Status', OPath: 'ref:Type' },
          { Name: 'ParentResource', OPath: 'ref:IdRefResourceParent' },
          { Name: 'ResourceType', OPath: 'ref:IdRefResourceType' },
          ...additionalQueryColumns,
        ],
        columnHeaders: [
          { dataField: 'Id', dataType: 'string', visible: false, allowFiltering: true, showInColumnChooser: false },
          {
            dataField: 'ObjectType',
            dataType: 'number',
            visible: false,
            allowFiltering: false,
            showInColumnChooser: false,
          },
          { dataField: 'Name', caption: this.res.get('Name') },
          { dataField: 'Kind', caption: this.res.get('Type') },
          { dataField: 'Status', caption: this.res.get('Status') },
          ...additionalGridColumns,
        ],
      },
      preselectedKeys: this.selectedResourceKeys,
    };
    return of([resToGenerateGroup]);
  }

  private objectTypeDesignations: Map<number, string> = new Map<number, string>();

  private calcObjectTypeDesignation(
    rowData: any,
    objectTypeDesignations: Map<number, string>,
    lookupNewObjectTypeTrigger$: BehaviorSubject<number>,
  ): string {
    const otNumber = Number(rowData.MasterObjectType);

    let otDesignation = objectTypeDesignations.get(otNumber);

    if (!otDesignation) {
      lookupNewObjectTypeTrigger$.next(otNumber);
      otDesignation = String(otNumber);
    }

    return otDesignation;
  }

  private getSelectedWorkGenerationInfos(): MtWorkGenerationInfo[] {
    const infosFromSelection: MtWorkGenerationInfo[] = [];

    for (const objSelDataRow of this.groupSelectionComponent.getSelectedDataRows(this.objSelectionGroupName)) {
      infosFromSelection.push({
        MtObjectId: objSelDataRow.Id,
        MtObjWorkTypeId: objSelDataRow.MtObjWorkTypeId,
        EarliestWorkExecDate:
          objSelDataRow.EarliestWorkExecDate == null
            ? this.workwizardOptions.executeEarliest
            : new Date(objSelDataRow.EarliestWorkExecDate),
        LatestWorkExecDate:
          objSelDataRow.LatestWorkExecDate == null
            ? this.workwizardOptions.executeLatest
            : new Date(objSelDataRow.LatestWorkExecDate),
        ScheduledWorkExecDate:
          objSelDataRow.ScheduledWorkExecDate == null
            ? this.workwizardOptions.scheduledExecutionDate
            : new Date(objSelDataRow.ScheduledWorkExecDate),
        WorkExecTime:
          objSelDataRow.WorkExecTimeInDays == null
            ? this.workwizardOptions.execTimeInDays
            : objSelDataRow.WorkExecTimeInDays,
      });
    }
    return infosFromSelection;
  }

  private generateWorks(): void {
    if (this.textButton() === this.res.get('ButtonTextClose')) {
      this.closeWizardDialog.emit();
    } else {
      this.firstTitle.set(this.res.get('OperationIsExecuted'));
      this.textButton.set(this.res.get('ButtonTextClose'));
      this.buttonStatus.set(!this.buttonStatus());
      this.displayBusybar.set(true);
      this.isEditable = false;
      this.updateSettingsFromWizardPages();

      const auftrag: MaintenanceOrder = {
        designation: this.newAuftragName,
        date: this.auftragsDate,
        state: this.auftragStatus,
        number: this.newAuftragNumber,
        remark: this.newAuftragRemark,
      };
      this.createNewAuftrag(auftrag);
    }
  }

  public generateWorksWithProtocol(): void {
    this.generateWorks();
    this.generateAssignedResourcesData();
  }

  private generateAssignedResourcesData(): void {
    if (this.selectedResourcesIds) {
      this.workService
        .queryResources()
        .then((resources: MtResource[]) => {
          const filteredResources = resources.filter((resource) => this.selectedResourcesIds.includes(resource.Id));
          this.matchingResources = filteredResources;
        })
        .catch((error) => {
          console.error('Error fetching resources', error);
        });
    } else {
      this.matchingResources = [];
    }
  }

  private generateProtocol(res?: ResultWorkGeneration): void {
    const protocolPopupTitle = this.res.get('OrdersCreationProtocolTitle');
    let categoryDescriptionInfos = new Map<string, string>();
    let customColumnInfos = new Map<string, _AbcGridColumn[]>();
    let result: any;

    if (this.auftragsNameResult) {
      result = {
        [this.res.get('GeneratedOrders')]: [] as any,
        [this.res.get('GeneratedWorks')]: [] as MtWork[],
        [this.res.get('AlreadyExistingWorks')]: [] as MtBase[],
        [this.res.get('AssignedResources')]: [] as MtResource[],
      };
    } else {
      result = {
        [this.res.get('GeneratedWorks')]: [] as MtWork[],
        [this.res.get('AlreadyExistingWorks')]: [] as MtBase[],
        [this.res.get('AssignedResources')]: [] as MtResource[],
      };
    }

    if (this.auftragsNameResult) {
      let statusText: string = '';
      if (this.auftragStatus) {
        statusText = this.statusList[+this.auftragStatus];
      }

      result[this.res.get('GeneratedOrders')] = [
        {
          Designation: this.auftragsNameResult,
          Number: this.newAuftragNumber,
          Date: this.auftragsDate,
          Status: statusText,
          Remark: this.newAuftragRemark,
        },
      ];
      categoryDescriptionInfos.set(this.res.get('GeneratedOrders'), this.res.get('GeneratedOrdersDescription'));
      customColumnInfos.set(this.res.get('GeneratedOrders'), [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'Number', caption: this.res.get('Number') },
        {
          dataField: 'Date',
          caption: this.res.get('Date'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        { dataField: 'Status', caption: this.res.get('Status') },
        { dataField: 'Remark', caption: this.res.get('Remark') },
      ]);
    }
    if (res) {
      if (res.generatedWorks.length > 0) {
        result[this.res.get('GeneratedWorks')] = res.generatedWorks;
        categoryDescriptionInfos.set(this.res.get('GeneratedWorks'), this.res.get('GeneratedWorksDescription'));
        customColumnInfos.set(this.res.get('GeneratedWorks'), [
          { dataField: 'Designation', caption: this.res.get('Designation') },
          { dataField: 'ObjectDesignation', caption: this.res.get('Asset') },
          { dataField: 'Status', caption: this.res.get('Status') },
          {
            dataField: 'ExecutionFrom',
            caption: this.res.get('ExecutionFrom'),
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'ExecutionPlanned',
            caption: this.res.get('ExecutionPlanned'),
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'ExecutionTo',
            caption: this.res.get('ExecutionTill'),
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          { dataField: 'Order', caption: this.res.get('Order') },
        ]);
      }

      if (res.excludedObjects.length > 0) {
        result[this.res.get('AlreadyExistingWorks')] = res.excludedObjects;
        categoryDescriptionInfos.set(
          this.res.get('AlreadyExistingWorks'),
          this.res.get('AlreadyExistingWorksDescription'),
        );
        customColumnInfos.set(this.res.get('AlreadyExistingWorks'), [
          { dataField: 'Designation', caption: this.res.get('Asset') },
          { dataField: 'State', caption: this.res.get('Status') },
          {
            dataField: 'EarliestExecutionDate',
            caption: this.res.get('ExecutionFrom'),
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'ScheduledExecutionDate',
            caption: this.res.get('ExecutionPlanned'),
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          {
            dataField: 'LatestExecutionDate',
            caption: this.res.get('ExecutionTill'),
            dataType: 'date',
            format: _AbcGridComponent.getColumnFormat('date'),
          },
          { dataField: 'Order', caption: this.res.get('Order') },
        ]);
      }
    }

    if (this.matchingResources.length > 0) {
      result[this.res.get('AssignedResources')] = this.matchingResources;
      categoryDescriptionInfos.set(this.res.get('AssignedResources'), this.res.get('AssignedResourcesDescription'));
      customColumnInfos.set(this.res.get('AssignedResources'), [
        {
          dataField: 'Name',
          caption: this.res.get('Name'),
        },
        { dataField: 'Art', caption: this.res.get('Type') },
        { dataField: 'Code', caption: this.res.get('Code') },
        { dataField: 'Status', caption: this.res.get('Status') },
      ]);
    }

    this.protocolViewerService.openProtocolViewer(
      result,
      customColumnInfos,
      categoryDescriptionInfos,
      protocolPopupTitle,
      undefined,
      undefined,
      true,
    );
  }

  private generateProtocolWithNewlyAssignedWorks(res?: ResultWorkGeneration): void {
    const protocolPopupTitle = this.res.get('OrdersCreationProtocolTitle');
    let categoryDescriptionInfos = new Map<string, string>();
    let customColumnInfos = new Map<string, _AbcGridColumn[]>();
    let result: any;

    if (this.auftragsNameResult) {
      result = {
        [this.res.get('GeneratedOrders')]: [] as any,
        [this.res.get('NewlyAssignedWorks')]: [] as MtBase[],
        [this.res.get('AssignedResources')]: [] as MtResource[],
      };
    } else {
      result = {
        [this.res.get('NewlyAssignedWorks')]: [] as MtBase[],
        [this.res.get('AssignedResources')]: [] as MtResource[],
      };
    }

    if (this.auftragsNameResult) {
      let statusText: string = '';
      if (this.auftragStatus) {
        statusText = this.statusList[+this.auftragStatus];
      }

      result[this.res.get('GeneratedOrders')] = [
        {
          Designation: this.auftragsNameResult,
          Number: this.newAuftragNumber,
          Date: this.auftragsDate,
          Status: statusText,
          Remark: this.newAuftragRemark,
        },
      ];
      categoryDescriptionInfos.set(this.res.get('GeneratedOrders'), this.res.get('GeneratedOrdersDescription'));
      customColumnInfos.set(this.res.get('GeneratedOrders'), [
        { dataField: 'Designation', caption: this.res.get('Designation') },
        { dataField: 'Number', caption: this.res.get('Number') },
        {
          dataField: 'Date',
          caption: this.res.get('Date'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        { dataField: 'Status', caption: this.res.get('Status') },
        { dataField: 'Remark', caption: this.res.get('Remark') },
      ]);
    }
    if (res) {
      result[this.res.get('NewlyAssignedWorks')] = res.excludedObjects;
      categoryDescriptionInfos.set(this.res.get('NewlyAssignedWorks'), this.res.get('NewlyAssignedWorksDescription'));
      customColumnInfos.set(this.res.get('NewlyAssignedWorks'), [
        { dataField: 'Designation', caption: this.res.get('Asset') },
        { dataField: 'State', caption: this.res.get('Status') },
        {
          dataField: 'EarliestExecutionDate',
          caption: this.res.get('ExecutionFrom'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        {
          dataField: 'ScheduledExecutionDate',
          caption: this.res.get('ExecutionPlanned'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        {
          dataField: 'LatestExecutionDate',
          caption: this.res.get('ExecutionTill'),
          dataType: 'date',
          format: _AbcGridComponent.getColumnFormat('date'),
        },
        { dataField: 'Order', caption: this.res.get('Order') },
        { dataField: 'ObjWorkTypeDesignation', caption: this.res.get('MaintenanceCycle') },
      ]);
    }

    if (this.matchingResources.length > 0) {
      result[this.res.get('AssignedResources')] = this.matchingResources;
      categoryDescriptionInfos.set(this.res.get('AssignedResources'), this.res.get('AssignedResourcesDescription'));
      customColumnInfos.set(this.res.get('AssignedResources'), [
        {
          dataField: 'Name',
          caption: this.res.get('Name'),
        },
        { dataField: 'Art', caption: this.res.get('Type') },
        { dataField: 'Code', caption: this.res.get('Code') },
        { dataField: 'Status', caption: this.res.get('Status') },
      ]);
    }

    this.protocolViewerService.openProtocolViewer(
      result,
      customColumnInfos,
      categoryDescriptionInfos,
      protocolPopupTitle,
      undefined,
      undefined,
      true,
    );
  }

  private updateSettingsFromWizardPages(): void {
    this.workwizardOptions.workTypeId = this.configurationComponent.workTypeId;
    this.workwizardOptions.orderId = this.auftragsId;

    this.workwizardOptions.scheduledExecutionDate = this.manualScheduledExecDate;
    this.workwizardOptions.executeEarliest = this.manualEarliestExecDate;
    this.workwizardOptions.executeLatest = this.manualLatestExecDate;
    this.workwizardOptions.execTimeInDays = this.manualExecTimeInDays;
    this.workwizardOptions.objectInfos = this.getSelectedWorkGenerationInfos();
    this.workwizardOptions.state = this.configurationComponent.status ?? WorkState.anstehend;
    this.workwizardOptions.workGeneratorMode = this.selectedGeneratorMode;
  }

  private showMessageAsHtml(htmlMsg: string, messageMode: MessageMode = MessageMode.Info): void {
    const settings = new MessageSettings();
    settings.mode = messageMode;
    let icon = MessageBoxIcon.Info;
    switch (messageMode) {
      case MessageMode.Error:
        icon = MessageBoxIcon.Error;
        break;
      case MessageMode.Warning:
        icon = MessageBoxIcon.Warning;
        break;
      case MessageMode.Question:
        icon = MessageBoxIcon.Question;
        break;
    }
    settings.icon = icon;
    settings.button = MessageBoxButtons.OK;

    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;

    if (this.alive) {
      this.messageBoxService
        .showMessageBox(settings)
        .pipe(takeWhile(() => this.alive))
        .subscribe((result: DialogResult) => {});
    }
  }

  private showWarningMessageAsHtml(htmlMsg: string, messageMode: MessageMode): void {
    const settings = new MessageSettings();
    settings.mode = messageMode;
    settings.icon = MessageBoxIcon.Warning;
    settings.button = MessageBoxButtons.YesNo;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;
    if (this.alive) {
      this.messageBoxService
        .showMessageBox(settings)
        .pipe(takeWhile(() => this.alive))
        .subscribe((resultObject: any) => {
          const result = resultObject.result;
          if (result === 6) {
            this.validateStepCore(this.stepObjectSelection, this.validateObjectSelectionStep(true));
          }
        });
    }
  }

  public onNewAuftragNameChanged(eventArgs: any): void {
    this.newAuftragName = ConvertHelper.toString(eventArgs.value);
  }

  public onNumberChanged(eventArgs: any): void {
    this.newAuftragNumber = eventArgs.value;
  }

  public onRemarkChanged(eventArgs: any): void {
    this.newAuftragRemark = eventArgs.value;
  }

  public onAuftragDateChanged(eventArgs: any): void {
    this.auftragsDate = new Date(ConvertHelper.toString(eventArgs));
    this.manualEarliestExecDate = this.auftragsDate;
    this.manualScheduledExecDate = this.auftragsDate;
  }

  public onAuftragStatusChanged(eventArgs: any): void {
    this.auftragStatus = ConvertHelper.toString(eventArgs.value);
  }

  private createNewAuftrag(auftrag: MaintenanceOrder): void {
    const auftragsName = auftrag.designation ?? '';
    const auftragsNumber = auftrag.number ?? '';

    this.auftragsNameResult = auftragsName;

    const selectedIdResources = this.groupSelectionComponent2.getSelectedKeys(this.objSelectionGroupName2);

    this.workService.queryExistingAuftrag(auftragsName, auftragsNumber).subscribe((results) => {
      const objsExisting: MtBase[] = JSON.parse(JSON.stringify(results));

      if (this.workService.isEmptyObject(objsExisting) || objsExisting.length === 0) {
        const insertAuftragQuery = this.workService.createAuftragPersistQuery(auftrag);

        this.abcCore.data.executePersistObjectQuery(insertAuftragQuery).subscribe(
          (res) => {
            const auftrag: MtBase = JSON.parse(JSON.stringify(res));
            this.onAuftragGenerated(auftrag.Id);

            selectedIdResources.forEach((resourceId) => {
              this.workService.assignResourceToOrder([resourceId], auftrag.Id ?? '');
            });
          },
          (err) => this.workService.onGenerationFailed(err),
        );
      } else {
        const msgError = "Der Auftrag '" + auftragsName + "' ist bereits in der Datenbank vorhanden!";
        this.workService.showErrorInfo(msgError);
        this.displayBusybar.set(false);
        this.buttonStatus.set(false);
        this.firstTitle.set(this.res.get('OperationFinishedWithErrors'));
      }
    });
  }

  private generateWorksCore(): void {
    const selectedIdResources = this.groupSelectionComponent2.getSelectedKeys(this.objSelectionGroupName2);

    this.workService.generateWorks(
      this.workwizardOptions.objectInfos,
      this.workwizardOptions.state ?? WorkState.anstehend,
      this.workwizardOptions.workTypeId ?? '',
      this.workwizardOptions.orderId ?? '',
      this.workwizardOptions.source ?? '',
      selectedIdResources,
    );
  }

  private assignExistingWorksToOrder(idsList: string[], orderId: string): void {
    this.workService.assignWorksToOrder(idsList, orderId);
  }

  private onAuftragGenerated(id: string | undefined): void {
    this.auftragsId = id ?? '';
    this.workwizardOptions.orderId = this.auftragsId;

    switch (this.workwizardOptions.workGeneratorMode) {
      case WorkGeneratorMode.cyclic:
      case WorkGeneratorMode.manual: {
        if (this.workwizardOptions.objectInfos.length > 0) {
          this.generateWorksCore();
        } else {
          this.generateAssignedResourcesData();
          this.generateProtocol();
          this.displayBusybar.set(false);
          this.buttonStatus.set(false);
        }

        break;
      }
      case WorkGeneratorMode.availableWorks: {
        let idsList: string[] = this.workwizardOptions.objectInfos.map((work) => work.MtObjectId);
        this.assignExistingWorksToOrder(idsList, this.workwizardOptions.orderId);

        let res: ResultWorkGeneration = new ResultWorkGeneration();
        const selectedWorks = this.groupSelectionComponent.getSelectedDataRows(this.objSelectionGroupName);
        const mtObjectWorkList: MtObjectWork[] = selectedWorks.map((item) => ({
          Designation: item.Anlage,
          State: item.Status,
          Order: this.auftragsNameResult,
          EarliestExecutionDate: item['Ausführung ab'],
          ScheduledExecutionDate: item['Ausführung geplant'],
          LatestExecutionDate: item['Ausführung bis'],
          ObjWorkTypeDesignation: item.ObjWorkType_Designation,
        }));

        res.excludedObjects = mtObjectWorkList;
        this.generateProtocolWithNewlyAssignedWorks(res);
        this.displayBusybar.set(false);
        this.buttonStatus.set(false);
        break;
      }
      default: {
        console.error('Invalid work generator mode');
        break;
      }
    }
  }

  public validateStep(step: MatStep): void {
    if (step === this.stepMainData) {
      this.validateStepCore(step, this.validateMainDataStep());
    } else if (step === this.stepResourcesSelection) {
      this.validateStepCore(step, '');
    } else if (step === this.stepObjectSelection) {
      this.selectedResourcesIds = this.groupSelectionComponent2.getSelectedKeys(this.objSelectionGroupName2);
      this.validateStepCore(step, this.validateObjectSelectionStep(false));
    } else if (step === this.stepAuftrag) {
      this.validateAuftragStep(step, this.newAuftragName ?? '', this.newAuftragNumber ?? '');
    }
  }

  private validateAuftragStep(step: MatStep, auftragName: string, auftragNumber: string): void {
    if (!auftragName) {
      let msg: string = '<div>Der Name des neuen Auftrags muss angegeben werden!</div>';
      this.validateStepCore(step, msg);
    } else {
      this.workService.queryExistingAuftrag(auftragName, auftragNumber).subscribe((results) => {
        let msg = '';
        const objsExisting: MtBase[] = JSON.parse(JSON.stringify(results));
        if (!(this.workService.isEmptyObject(objsExisting) || objsExisting.length === 0))
          msg = "Der Auftrag '" + auftragName + "' ist bereits in der Datenbank vorhanden!";

        this.validateStepCore(step, msg);
      });
    }
  }

  private validateStepCore(step: MatStep, msg: string): void {
    step.completed = msg === null || msg.trim().length === 0;

    if (step.completed) {
      this.stepper.next();
    } else {
      if (step.stepLabel) {
        this.workService.showWarningInfo(msg);
      } else {
        this.workService.showErrorInfo(msg);
      }
    }
  }

  private validateMainDataStep(): string {
    let msg: string = '';
    if (this.selectedGeneratorMode !== WorkGeneratorMode.availableWorks) {
      if (!this.configurationComponent?.workTypeId) {
        msg += '<div>Eine Arbeitsvorlage muss ausgewählt werden!</div>';
      }

      if (this.configurationComponent?.status === undefined) {
        msg += '<div>Der Status für die zu generierenden Arbeiten muss ausgewählt werden!</div>';
      }
    }
    if (
      this.selectedGeneratorMode === WorkGeneratorMode.manual &&
      !this.manualEarliestExecDate &&
      !this.manualScheduledExecDate &&
      !this.manualLatestExecDate
    ) {
      msg += '<div>Mindestens ein Ausführungsdatum muss angegeben werden!</div>';
    }

    return msg;
  }

  private validateObjectSelectionStep(buttonClicked: boolean): string {
    let msg: string = '';

    const selectedIds = this.groupSelectionComponent.getSelectedKeys(this.objSelectionGroupName);
    if (!buttonClicked) {
      if (!Array.isArray(selectedIds) || !selectedIds.length) {
        msg += `<div>${this.res.get('CreatedOrderWithoutWorks')}</div>`;
      }
    }

    return msg;
  }

  public moveBack(): void {
    this.stepper.previous();
  }

  public countAssets(assetsCount: number): void {
    this.assetsCount.set(assetsCount);
  }

  public countResources(resCount: number): void {
    this.resCount.set(resCount);
    this.selectedResourceKeys = this.groupSelectionComponent2?.getSelectedKeys(this.objSelectionGroupName2);
  }

  public onChildResourcesSelectionChange(e: ValueChangedEvent) {
    this.selectChildrenResources = e.value;
  }

  public onReferencedResourcesSelectionChange(e: ValueChangedEvent) {
    this.selectReferencedResources = e.value;
  }
}
